import { dump, IExif, insert, TagValues } from 'src/easyclaim.app.libs/piexif';
import { reportError } from 'src/easyclaim.app.libs/reportError';

import { buildExifIfd } from './buildExifIfd';
import { buildGpsIfd } from './buildGpsIfd';
import { parseUserAgent } from './parseUserAgent';

export interface DeviceInfo {
	make: string;
	model: string;
	fStop: number | null;
	exposureTime: number | null;
	isoSpeed: number | null;
	focalLength: number | null;
	subjectDistance: number | null;
	flashMode: number | null;
}

export const getCurrentPosition = (): Promise<GeolocationPosition | null> => {
	return new Promise((resolve) => {
		if (!navigator.geolocation) {
			reportError('Geolocation not supported', {
				context: 'cameraExifUtils.getCurrentPosition',
			});
			resolve(null);

			return;
		}
		navigator.geolocation.getCurrentPosition(
			(position) => resolve(position),
			(error) => {
				reportError(`Geolocation error: ${error.message}`, {
					context: 'cameraExifUtils.getCurrentPosition',
				});
				resolve(null);
			},
			{ enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 }
		);
	});
};

interface NavigatorUAData {
	getHighEntropyValues: (
		hints: string[]
	) => Promise<{ model?: string; platform?: string; platformVersion?: string }>;
}

interface NavigatorWithUAData extends Navigator {
	userAgentData?: NavigatorUAData;
}

const getDeviceInfo = async (): Promise<DeviceInfo> => {
	let make = 'Unknown';
	let model = 'Unknown';

	try {
		const nav = navigator as NavigatorWithUAData;

		if (nav.userAgentData && typeof nav.userAgentData.getHighEntropyValues === 'function') {
			const ua = await nav.userAgentData.getHighEntropyValues([
				'model',
				'platform',
				'platformVersion',
			]);

			make = ua.platform || 'Unknown';
			model = ua.model || 'Unknown';
		}
	} catch (e) {
		reportError(`Could not get high entropy user agent data: ${e}`, {
			context: 'cameraExifUtils.getDeviceInfo',
		});
	}

	if (make === 'Unknown') {
		({ make, model } = parseUserAgent(navigator.userAgent));
	}

	return {
		make,
		model,
		fStop: null,
		exposureTime: null,
		isoSpeed: null,
		focalLength: null,
		subjectDistance: null,
		flashMode: null,
	};
};

export const embedExifMetadata = async (
	dataUrl: string,
	position: GeolocationPosition | null,
	trackInfo?: Partial<DeviceInfo>
): Promise<string> => {
	try {
		const now = new Date();
		const zerothIFD: Record<number, unknown> = {};
		const exifIFD: Record<number, unknown> = {};
		const gpsIFD: Record<number, unknown> = {};
		const exifObj: IExif = { '0th': zerothIFD, Exif: exifIFD, GPS: gpsIFD, '1st': {} };

		zerothIFD[TagValues.ImageIFD.Software] = 'EasyClaim Camera';
		exifIFD[TagValues.ExifIFD.ColorSpace] = 1; // sRGB

		const deviceInfo = await getDeviceInfo();

		zerothIFD[TagValues.ImageIFD.Make] = deviceInfo.make;
		zerothIFD[TagValues.ImageIFD.Model] = deviceInfo.model;

		Object.assign(
			exifIFD,
			buildExifIfd({
				fStop: trackInfo?.fStop ?? deviceInfo.fStop,
				exposureTime: trackInfo?.exposureTime ?? deviceInfo.exposureTime,
				isoSpeed: trackInfo?.isoSpeed ?? deviceInfo.isoSpeed,
				focalLength: trackInfo?.focalLength ?? deviceInfo.focalLength,
				subjectDistance: trackInfo?.subjectDistance ?? deviceInfo.subjectDistance,
				flashMode: trackInfo?.flashMode ?? deviceInfo.flashMode,
			})
		);

		if (position) {
			Object.assign(gpsIFD, buildGpsIfd(position, now));
		}

		const exifBytes = dump(exifObj);

		return insert(exifBytes, dataUrl);
	} catch (err) {
		reportError(`Failed to embed EXIF metadata: ${err}`, {
			context: 'cameraExifUtils.embedExifMetadata',
		});

		return dataUrl;
	}
};
